import type { Notification } from '@crm/contracts';
import {
  AlarmClock,
  Bell,
  BellRing,
  CalendarClock,
  CheckCheck,
  ListChecks,
  PhoneIncoming,
  Target,
  UserPlus,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QueryBoundary } from '@/components/query-boundary';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Segmented } from '@/components/ui/segmented';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from '@/features/notifications/use-notifications';
import { formatRelative } from '@/lib/datetime';
import { cn } from '@/lib/utils';

/**
 * The header bell: a badge, and a panel of what the reminder sweep has sent.
 *
 * Self-contained by design — it takes no props and reads everything it needs
 * from the query cache, so it drops into the app shell as a single element.
 *
 * The badge is the whole point of the notifications table. Reminders are
 * delivered by writing a row rather than pushing to a socket, which means the
 * count is correct whenever the operator next opens the app, including after
 * the tab was closed all night.
 */

const TYPE_ICON: Record<Notification['notificationType'], ComponentType<{ className?: string }>> = {
  follow_up_due: CalendarClock,
  follow_up_overdue: AlarmClock,
  follow_up_chase: AlarmClock,
  follow_up_digest: ListChecks,
  lead_assigned: UserPlus,
  target_set: Target,
  call_received: PhoneIncoming,
};

/** Past 99 the exact number stops being information and starts being noise. */
const badgeLabel = (n: number) => (n > 99 ? '99+' : String(n));

const FILTERS = [
  { value: 'unread', label: 'Unread' },
  { value: 'all', label: 'All' },
];

function NotificationRow({ item, onOpen }: { item: Notification; onOpen: (item: Notification) => void }) {
  const Icon = TYPE_ICON[item.notificationType];
  const unread = item.readAt === null;

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2',
          unread && 'bg-brand-soft/40',
        )}
      >
        <span
          className={cn(
            'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full',
            item.notificationType === 'follow_up_overdue' ? 'bg-danger-soft text-danger' : 'bg-surface-2 text-muted',
          )}
        >
          <Icon className="size-[15px]" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={cn('min-w-0 flex-1 truncate text-sm text-ink', unread ? 'font-bold' : 'font-semibold')}>
              {item.title}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-muted">{formatRelative(item.createdAt)}</span>
          </span>
          {item.body && <span className="mt-0.5 block truncate text-[13px] text-muted">{item.body}</span>}
        </span>

        {unread && <span className="mt-2 size-2 shrink-0 rounded-full bg-brand" aria-label="Unread" role="img" />}
      </button>
    </li>
  );
}

function PanelSkeleton() {
  return (
    <div className="space-y-3 p-4" aria-busy="true">
      <span className="sr-only">Loading notifications…</span>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('unread');
  const unreadOnly = filter === 'unread';

  const count = useUnreadCount();
  // Only fetched while the panel is open — the badge alone does not need the
  // rows, and the bell sits on every screen in the app.
  const list = useNotifications(unreadOnly, open);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unread = count.data?.unread ?? 0;

  /**
   * Clicking marks read and goes to the work.
   *
   * The destination is the contact profile, which is where the follow-up is
   * actionable — it carries the follow-ups panel, the timeline and the call
   * and WhatsApp actions. There is no standalone /follow-ups/:id screen to
   * send them to, and dropping them on the calendar with no row highlighted
   * would make them hunt for what they just clicked.
   */
  function openNotification(item: Notification) {
    if (item.readAt === null) markRead.mutate(item.id);
    setOpen(false);
    navigate(item.contactId ? `/contacts/${item.contactId}` : '/follow-ups');
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          {unread > 0 ? <BellRing /> : <Bell />}
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 grid min-w-[18px] place-items-center rounded-full bg-danger px-1 text-[10px] font-bold leading-[18px] text-white"
            >
              {badgeLabel(unread)}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="flex flex-col p-0">
        <div className="flex items-center gap-3 border-b border-line px-4 py-3">
          <h2 className="text-[15px] font-bold tracking-tight text-ink">Notifications</h2>
          {unread > 0 && (
            <Badge tone="danger" className="tabular-nums">
              {badgeLabel(unread)}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => markAll.mutate()}
            loading={markAll.isPending}
            disabled={unread === 0}
          >
            <CheckCheck /> Mark all read
          </Button>
        </div>

        <div className="border-b border-line px-4 py-2.5">
          <Segmented
            name="notification-filter"
            options={FILTERS}
            value={filter}
            onValueChange={setFilter}
            aria-label="Filter notifications"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <QueryBoundary
            query={list}
            loading={<PanelSkeleton />}
            empty={{
              when: (data) => data.items.length === 0,
              render: (
                <p className="px-4 py-10 text-center text-sm text-muted">
                  {unreadOnly ? 'Nothing unread. Reminders land here.' : 'No notifications yet.'}
                </p>
              ),
            }}
          >
            {(data) => (
              <ul className="divide-y divide-line">
                {data.items.map((item) => (
                  <NotificationRow key={item.id} item={item} onOpen={openNotification} />
                ))}
              </ul>
            )}
          </QueryBoundary>
        </div>
      </PopoverContent>
    </Popover>
  );
}
